import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import './AssetsReviews.css';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend
} from 'recharts';

const AssetsReviews = () => {
  const { token } = useParams();
  const [asset, setAsset] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssetAndReviews = async () => {
      try {
        const assetRef = doc(db, 'assets', token);
        const assetSnap = await getDoc(assetRef);

        if (!assetSnap.exists()) {
          console.warn("No asset found with token:", token);
          setLoading(false);
          return;
        }

        const assetData = assetSnap.data();
        const assetId = assetSnap.id;
        setAsset({ id: assetId, ...assetData });

        const q = query(collection(db, 'asset_reviews'), where('asset_id', '==', assetId));
        const snapshot = await getDocs(q);

        const issueData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);

        setIssues(issueData);
      } catch (err) {
        console.error("Error fetching asset or issues:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssetAndReviews();
  }, [token]);

  const sentimentStats = {
    '😊': 0,
    '😐': 0,
    '😠': 0
  };

  issues.forEach((issue) => {
    const emoji = issue.sentiment?.trim();
    if (sentimentStats.hasOwnProperty(emoji)) {
      sentimentStats[emoji]++;
    }
  });

  const sentimentData = Object.entries(sentimentStats).map(([name, value]) => ({
    name,
    value
  }));

  const COLORS = ['#00C49F', '#FFBB28', '#FF4C4C'];

  return (
    <div className="asset-reviews-wrapper">
      <div className="asset-reviews-container">
        <h2 className="text-3xl font-bold mb-4">🛠 Asset Reviews</h2>

        {loading && <p>Loading...</p>}

        {!loading && !asset && (
          <p className="text-red-600">Asset not found.</p>
        )}

        {asset && (
          <div className="asset-header mb-6 bg-white p-4 rounded shadow">
            <p><strong>Serial:</strong> {asset.serial_number}</p>
            <p><strong>Model:</strong> {asset.model}</p>
            <p><strong>Location:</strong> {asset.location}</p>
          </div>
        )}

        {issues.length > 0 && (
          <div className="mb-6 bg-white p-4 rounded shadow w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Sentiment Overview</h3>
            <PieChart width={300} height={250}>
              <Pie
                data={sentimentData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                nameKey="name"
                label
              >
                {sentimentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </div>
        )}

        {!loading && issues.length === 0 && (
          <p>No issues or feedback submitted for this asset yet.</p>
        )}

        {issues.length > 0 && (
          <div className="issues-list space-y-4">
            {issues.map((issue, index) => {
              console.log(`Issue #${index + 1}`, issue);

              return (
                <div
                  key={issue.id}
                  className="review-card"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  <p className="text-gray-800 mb-2">
                    📝 {issue.comment && typeof issue.comment === 'string'
                      ? issue.comment
                      : <em className="text-gray-400">No comment provided</em>}
                  </p>

                  <p className="text-sm text-gray-600">
                    🕒 {issue.createdAt?.toDate?.().toLocaleString?.() || 'No timestamp'}
                  </p>

                  <p className="text-xl">Sentiment: {issue.sentiment || '😐'}</p>

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetsReviews;
